import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, Loader2, Sparkles, ClipboardPaste, Eraser, Play, LogIn, Keyboard } from 'lucide-react';
import Navbar from '../components/Navbar';
import PuzzleCropModal from '../components/PuzzleCropModal';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY = () => Array.from({ length: 9 }, () => Array(9).fill(0));

// Helper: 81-digit string → 9x9 array; null on invalid input
function stringToGrid(text) {
  const cleaned = (text || '').replace(/[^0-9.]/g, '').replace(/\./g, '0');
  if (cleaned.length !== 81) return null;
  const grid = EMPTY();
  for (let r = 0; r < 9; r++) {
    for (let c = 0; c < 9; c++) grid[r][c] = parseInt(cleaned[r * 9 + c], 10);
  }
  return grid;
}

function gridToString(grid) {
  return grid.map((row) => row.join('')).join('');
}

export default function ScanPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Editor state
  const [grid, setGrid] = useState(EMPTY());
  const [activeCell, setActiveCell] = useState(null);
  // Fast-type mode: keyboard input flows directly into the active cell and
  // auto-advances. Off by default so the existing tap-then-numpad workflow
  // is unchanged. Both modes share the same grid + activeCell state — the
  // numpad just hides when fast mode is on, and a hidden numeric input is
  // focused to summon the iOS keyboard.
  const [fastType, setFastType] = useState(false);
  const fastTypeInputRef = useRef(null);

  // Crop + OCR pipeline state
  const [pendingCropFile, setPendingCropFile] = useState(null);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrInfo, setOcrInfo] = useState(null); // { confidence, quota_used, quota_limit }

  // Play (validate + create puzzle) state
  const [playBusy, setPlayBusy] = useState(false);
  // Cells the server flagged as ambiguous (multiple-solutions case).
  // Highlighted in red so the user can immediately spot the misplaced clue.
  const [ambiguousCells, setAmbiguousCells] = useState([]);
  const ambiguousKeys = React.useMemo(
    () => new Set(ambiguousCells.map((c) => `${c.row},${c.col}`)),
    [ambiguousCells]
  );
  const isAmbiguous = (r, c) => ambiguousKeys.has(`${r},${c}`);

  const setCell = (r, c, v) => {
    setGrid((g) => g.map((row, ri) => row.map((val, ci) => (ri === r && ci === c ? v : val))));
    // Any edit invalidates the previous ambiguity feedback — clear it so
    // the red highlight only reflects the CURRENT grid state.
    if (ambiguousCells.length > 0) setAmbiguousCells([]);
  };

  // Compute the next cell in reading order, or null at the end.
  const nextCell = (r, c) => {
    if (c < 8) return { r, c: c + 1 };
    if (r < 8) return { r: r + 1, c: 0 };
    return null;
  };
  const prevCell = (r, c) => {
    if (c > 0) return { r, c: c - 1 };
    if (r > 0) return { r: r - 1, c: 8 };
    return null;
  };

  // Global keyboard handler — only active when fastType is on. Digit 1-9
  // fills + advances; 0 / Space / "." leaves cell blank + advances; Backspace
  // clears current cell and moves to the previous cell; arrow keys navigate
  // without changing; Enter / Tab advances without changing.
  useEffect(() => {
    if (!fastType) return undefined;
    const onKey = (e) => {
      if (!activeCell) return;
      const { r, c } = activeCell;
      if (e.key >= '1' && e.key <= '9') {
        setCell(r, c, parseInt(e.key, 10));
        const n = nextCell(r, c);
        if (n) setActiveCell(n);
        e.preventDefault();
      } else if (e.key === '0' || e.key === ' ' || e.key === '.') {
        setCell(r, c, 0);
        const n = nextCell(r, c);
        if (n) setActiveCell(n);
        e.preventDefault();
      } else if (e.key === 'Backspace') {
        // Two-step: if current cell is non-empty, just clear it. If already
        // empty, also step back to previous so backspace feels like "undo".
        const wasEmpty = grid[r][c] === 0;
        setCell(r, c, 0);
        if (wasEmpty) {
          const p = prevCell(r, c);
          if (p) setActiveCell(p);
        }
        e.preventDefault();
      } else if (e.key === 'ArrowRight' || e.key === 'Enter' || e.key === 'Tab') {
        const n = nextCell(r, c);
        if (n) setActiveCell(n);
        e.preventDefault();
      } else if (e.key === 'ArrowLeft') {
        const p = prevCell(r, c);
        if (p) setActiveCell(p);
        e.preventDefault();
      } else if (e.key === 'ArrowUp' && r > 0) {
        setActiveCell({ r: r - 1, c });
        e.preventDefault();
      } else if (e.key === 'ArrowDown' && r < 8) {
        setActiveCell({ r: r + 1, c });
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fastType, activeCell, grid]);

  // When fast-type mode flips on, focus the hidden numeric input so the
  // iOS keyboard appears. Cursor positioning is handled by the toggle's
  // onClick (so we don't have to setState inside an effect).
  useEffect(() => {
    if (!fastType) return undefined;
    const t = setTimeout(() => {
      if (fastTypeInputRef.current) fastTypeInputRef.current.focus();
    }, 50);
    return () => clearTimeout(t);
  }, [fastType]);

  const clearGrid = () => {
    setGrid(EMPTY());
    setActiveCell(null);
    setOcrInfo(null);
  };

  const pasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const parsed = stringToGrid(text);
      if (!parsed) {
        toast.error('Clipboard does not contain 81 digits.');
        return;
      }
      setGrid(parsed);
      setActiveCell({ r: 0, c: 0 });
      toast.success('Pasted from clipboard.');
    } catch {
      toast.error('Clipboard access denied — paste manually.');
    }
  };

  const handlePhoto = (file) => {
    if (!file) return;
    if (!user) {
      toast.info('Sign in to scan a photo');
      return;
    }
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
      toast.error('Use a JPEG, PNG, or WEBP image.');
      return;
    }
    setPendingCropFile(file);
  };

  const handleCroppedBlob = async (blob) => {
    setPendingCropFile(null);
    if (!blob) return;
    setOcrBusy(true);
    setOcrInfo(null);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || '').split(',')[1] || '');
        reader.onerror = () => reject(new Error('file-read-failed'));
        reader.readAsDataURL(blob);
      });
      const { data } = await axios.post(
        `${API_URL}/api/puzzle-ocr`,
        { image_base64: base64, mime_type: 'image/jpeg' },
        { withCredentials: true }
      );
      if (!data.ok || !data.grid) {
        toast.error(data.message || 'Could not read a sudoku grid from that photo. Try a tighter crop on just the 9x9 grid.');
        return;
      }
      const parsed = stringToGrid(data.grid);
      if (!parsed) {
        toast.error('OCR returned an unparseable grid.');
        return;
      }
      setGrid(parsed);
      setActiveCell({ r: 0, c: 0 });
      setOcrInfo({
        confidence: data.confidence,
        quota_used: data.quota_used,
        quota_limit: data.quota_limit,
      });
      const conf = data.confidence === 'high' ? 'High' : data.confidence === 'medium' ? 'Medium' : 'Low';
      const quota = data.quota_limit > 0 ? ` (${data.quota_used}/${data.quota_limit} today)` : '';
      toast.success(`${conf} confidence${quota} — please proofread, then tap Play.`);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || 'Failed to process image';
      toast.error(detail);
    } finally {
      setOcrBusy(false);
    }
  };

  // AbortController for the /api/puzzles/from-scan POST. If the user
  // navigates away while the backend is still validating (uniqueness
  // check + grade), we cancel the request — both to stop wasting
  // backend cycles AND to silence "setState on unmounted component"
  // warnings that React surfaces when the late response tries to
  // navigate() / toast() from a component that's already gone.
  const playAbortRef = useRef(null);
  useEffect(() => {
    return () => {
      if (playAbortRef.current) playAbortRef.current.abort();
    };
  }, []);

  const playThisPuzzle = async () => {
    const compact = gridToString(grid);
    const filled = compact.replace(/0/g, '').length;
    if (filled < 17) {
      toast.error('A valid sudoku needs at least 17 clues. Add more before playing.');
      return;
    }
    // Clear any prior highlights before revalidating.
    setAmbiguousCells([]);
    // Abort any previous in-flight request before starting a new one.
    if (playAbortRef.current) playAbortRef.current.abort();
    playAbortRef.current = new AbortController();
    setPlayBusy(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/puzzles/from-scan`,
        { grid: compact, source: 'scan' },
        { withCredentials: true, signal: playAbortRef.current.signal }
      );
      if (data.warning) {
        toast.warning(data.warning);
      } else {
        toast.success(`Filed at Level ${data.difficulty_level} — ${data.difficulty_name}`);
      }
      // Navigate into the existing game UI; auto-start there.
      navigate(`/play/${data.id}`);
    } catch (e) {
      // Silently swallow cancellations (user navigated away on purpose).
      if (axios.isCancel?.(e) || e.name === 'CanceledError' || e.code === 'ERR_CANCELED') {
        return;
      }
      // Structured error path — server returned ambiguous_cells so we can
      // highlight the exact spots where the grid diverges.
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.reason === 'multiple_solutions') {
        setAmbiguousCells(detail.ambiguous_cells || []);
        toast.error(detail.message || 'Grid has multiple solutions — check the highlighted cells.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Could not start this puzzle');
      }
    } finally {
      setPlayBusy(false);
    }
  };

  if (authLoading) return null;

  // Logged-out state: encourage sign-in
  if (!user) {
    return (
      <div className="min-h-screen bg-[var(--background-page)]">
        <Navbar />
        <main className="max-w-2xl mx-auto px-4 py-10">
          <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)]" data-testid="scan-signin-card">
            <CardHeader>
              <CardTitle className="text-[var(--text-primary)] flex items-center gap-2 font-heading">
                <Camera className="w-5 h-5 text-[#A46944]" />
                Type & Play
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
                Got a sudoku from a newspaper, book, or magazine? Type it in,
                tap Play, and we&apos;ll grade it, hint it, and walk you through
                the deductions step-by-step.
              </p>
              <ul className="text-xs text-[var(--text-disabled)] list-disc pl-5 space-y-1">
                <li>Pure-logic solver with progressive hints</li>
                <li>Difficulty graded automatically (L1 – L17)</li>
                <li>Step-by-step walkthroughs of every technique</li>
              </ul>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 px-4 py-2 rounded bg-[var(--accent-primary)] text-white font-medium"
                data-testid="scan-signin-btn"
              >
                <LogIn className="w-4 h-4" /> Sign in to play
              </Link>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // Logged-in: full scan + review flow
  return (
    <div className="min-h-screen bg-[var(--background-page)]">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        <div data-testid="scan-page-root">
          <h1 className="text-3xl sm:text-4xl font-heading text-[var(--text-primary)] mb-2">
            Type & Play
          </h1>
          <p className="text-sm text-[var(--text-muted)] mb-6 max-w-xl">
            Type the digits of any printed sudoku, tap an empty cell to fill it,
            then hit Play. We&apos;ll grade the puzzle and drop you straight into
            the game.
          </p>
        </div>

        <Card
          className="bg-[var(--background-surface)] border-[var(--border-subtle)]"
          data-testid="scan-card"
        >
          <CardContent className="space-y-4 pt-6">
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={pasteFromClipboard}
                className="flex-1"
                data-testid="scan-paste-btn"
              >
                <ClipboardPaste className="w-4 h-4 mr-2" /> Paste 81 digits
              </Button>
              <Button
                variant="outline"
                onClick={clearGrid}
                className="flex-1"
                data-testid="scan-clear-btn"
              >
                <Eraser className="w-4 h-4 mr-2" /> Clear
              </Button>
            </div>
            <Button
              variant={fastType ? 'default' : 'outline'}
              onClick={() => {
                setFastType((v) => {
                  const next = !v;
                  if (next && !activeCell) setActiveCell({ r: 0, c: 0 });
                  return next;
                });
              }}
              className="w-full"
              data-testid="scan-fasttype-toggle"
              title="Type digits straight in — 0/space/period leaves the cell empty"
            >
              <Keyboard className="w-4 h-4 mr-2" />
              {fastType ? 'Fast-type ON — keys flow into cells' : 'Fast-type mode (type digits, advance)'}
            </Button>
            {fastType && (
              <p className="text-xs text-[var(--text-disabled)] leading-relaxed -mt-2">
                Cursor is at the highlighted cell. Press <span className="font-mono">1–9</span> to fill,{' '}
                <span className="font-mono">0</span> / <span className="font-mono">Space</span> /{' '}
                <span className="font-mono">.</span> to leave empty and advance,{' '}
                <span className="font-mono">Backspace</span> to step back, arrows to move freely.
              </p>
            )}
            {ocrInfo && (
              <div className="text-xs text-[var(--text-muted)] flex items-center gap-2" data-testid="scan-ocr-info">
                <Sparkles className="w-3.5 h-3.5 text-[#A46944]" />
                OCR <span className={
                  ocrInfo.confidence === 'high' ? 'text-emerald-400' :
                  ocrInfo.confidence === 'medium' ? 'text-amber-400' :
                  'text-red-400'
                }>{ocrInfo.confidence} confidence</span>
                {ocrInfo.quota_limit > 0 && (
                  <span className="text-[var(--text-disabled)]">
                    · {ocrInfo.quota_used}/{ocrInfo.quota_limit} scans today
                  </span>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Editable preview grid */}
        <Card
          className="bg-[var(--background-surface)] border-[var(--border-subtle)]"
          data-testid="scan-grid-card"
        >
          <CardHeader>
            <CardTitle className="text-sm text-[var(--text-muted)] font-normal">
              Review and edit before playing
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className="grid grid-cols-9 gap-px bg-[var(--border-medium)] p-px rounded mx-auto"
              style={{
                maxWidth: '500px',
                aspectRatio: '1',
                // Force every row to share the container height equally
                // so filling a cell with a digit doesn't push the cell's
                // row taller (and squeeze the rows below). Without this,
                // CSS grid sizes rows to content, which made the 9th row
                // collapse to a slit once rows 1-8 had digits in them.
                gridTemplateRows: 'repeat(9, 1fr)',
              }}
              data-testid="scan-grid"
            >
              {grid.flatMap((row, r) =>
                row.map((v, c) => {
                  const isActive = activeCell && activeCell.r === r && activeCell.c === c;
                  const ambig = isAmbiguous(r, c);
                  // Heavy 3x3 borders
                  const borderR = (c + 1) % 3 === 0 && c !== 8 ? 'border-r-2 border-r-[var(--border-strong)]' : '';
                  const borderB = (r + 1) % 3 === 0 && r !== 8 ? 'border-b-2 border-b-[var(--border-strong)]' : '';
                  return (
                    <button
                      key={`${r}-${c}`}
                      onClick={() => setActiveCell({ r, c })}
                      className={`flex items-center justify-center text-base font-mono transition-colors ${borderR} ${borderB} ${
                        isActive
                          ? 'bg-[#8B5A3C]/25 text-white'
                          : ambig
                          ? 'bg-[var(--error)]/20 text-[var(--error)] ring-2 ring-inset ring-[var(--error)]/70 font-semibold'
                          : v === 0
                          ? 'bg-[var(--background-surface)] text-[var(--text-disabled)]'
                          : 'bg-[var(--background-surface)] text-[var(--text-primary)] font-semibold'
                      }`}
                      data-testid={`scan-cell-${r}-${c}`}
                      data-ambiguous={ambig ? 'true' : undefined}
                    >
                      {v === 0 ? '' : v}
                    </button>
                  );
                })
              )}
            </div>
            {/* Numpad — hidden in fast-type mode (keyboard handles input). */}
            {!fastType && (
              <>
                <div className="grid grid-cols-9 gap-1 mt-4 max-w-[500px] mx-auto">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                    <button
                      key={n}
                      onClick={() => {
                        if (!activeCell) return;
                        setCell(activeCell.r, activeCell.c, n);
                        const next = nextCell(activeCell.r, activeCell.c);
                        if (next) setActiveCell(next);
                      }}
                      className="py-2 rounded bg-[var(--background-elevated)] hover:bg-[var(--hover-bg)] text-[var(--text-primary)] font-mono"
                      data-testid={`scan-num-${n}`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => activeCell && setCell(activeCell.r, activeCell.c, 0)}
                  className="w-full mt-2 py-2 rounded bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border border-amber-500/30 font-medium text-sm flex items-center justify-center gap-2 max-w-[500px] mx-auto"
                  data-testid="scan-num-clear"
                >
                  <Eraser className="w-4 h-4" /> Clear cell
                </button>
              </>
            )}
            {/* Hidden numeric input — focused in fast-type mode so the iOS
                keyboard appears. Stays invisibly under the grid so keystrokes
                are captured by the window listener (input's own value is
                meaningless — we read e.key in the keydown handler). */}
            {fastType && (
              <input
                ref={fastTypeInputRef}
                type="text"
                inputMode="numeric"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                value=""
                onChange={() => { /* handled in window keydown */ }}
                className="opacity-0 absolute -z-10 pointer-events-none w-0 h-0"
                aria-label="Fast-type input"
                data-testid="scan-fasttype-input"
              />
            )}
          </CardContent>
        </Card>

        {/* Add-a-Clue helper — appears only after a from-scan attempt
            revealed that the current grid has multiple valid solutions.
            The server returned ambiguous_cells: for each cell it also
            told us which two digits could satisfy it. The user picks
            the digit that matches their source and we re-validate. One
            correct pick usually collapses the whole ambiguity because
            constraints propagate. */}
        {ambiguousCells.length > 0 && (
          <Card
            className="bg-[var(--background-surface)] border-[#C4573E]/40"
            data-testid="add-clue-card"
          >
            <CardContent className="pt-6">
              <h3 className="font-heading text-base font-bold text-[var(--text-primary)] mb-1">
                Multiple solutions — please add a clue
              </h3>
              <p className="text-xs text-[var(--text-secondary)] mb-4">
                Compare each row/column below to your source. Pick the digit
                that matches — usually one pick resolves the whole grid.
              </p>
              <div className="space-y-2 mb-4">
                {ambiguousCells.map((cell) => (
                  <div
                    key={`${cell.row}-${cell.col}`}
                    className="flex items-center justify-between gap-3 p-2 rounded bg-[var(--background-elevated)]"
                    data-testid={`ambig-${cell.row}-${cell.col}`}
                  >
                    <span className="text-sm text-[var(--text-primary)] font-mono">
                      Row {cell.row + 1}, Col {cell.col + 1}
                    </span>
                    <div className="flex gap-1.5">
                      {cell.options.map((opt) => (
                        <button
                          key={opt}
                          onClick={() => setCell(cell.row, cell.col, opt)}
                          className="w-8 h-8 rounded font-mono font-bold text-sm bg-[#8B5A3C] hover:bg-[#A46944] text-white transition-colors"
                          data-testid={`ambig-pick-${cell.row}-${cell.col}-${opt}`}
                          title={`Set Row ${cell.row + 1}, Col ${cell.col + 1} to ${opt}`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-[var(--text-disabled)]">
                After picking, tap Play again to re-verify.
              </p>
            </CardContent>
          </Card>
        )}

        <Button
          onClick={playThisPuzzle}
          disabled={playBusy}
          className="w-full py-6 text-base font-medium bg-[var(--accent-primary)] hover:bg-[var(--accent-primary-hover)] text-white"
          data-testid="scan-play-btn"
        >
          {playBusy ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Validating and starting…
            </>
          ) : (
            <>
              <Play className="w-5 h-5 mr-2" /> Play this puzzle
            </>
          )}
        </Button>

        <p className="text-xs text-[var(--text-disabled)] text-center">
          We&apos;ll verify the puzzle has a unique solution before starting. If OCR missed a
          digit, edit the grid above first.
        </p>
      </main>

      {pendingCropFile && (
        <PuzzleCropModal
          imageFile={pendingCropFile}
          onCancel={() => setPendingCropFile(null)}
          onConfirm={handleCroppedBlob}
        />
      )}
    </div>
  );
}
